import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, MapPin, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { API, WHATSAPP_LINK, ADMIN_EMAIL, PHONE_DISPLAY, PHONE_TEL, BUSINESS_HOURS, OFFICE_ADDRESS, isBusinessHours, PACKAGES, scrollToSection } from "../lib/site";

const empty = { name: "", email: "", phone: "", brand: "", package: "Not sure yet", message: "" };

const inputCls =
  "w-full bg-transparent border border-coal/15 px-5 py-4 text-sm text-coal placeholder:text-coal/30 focus:outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta transition-colors duration-300";

export const Contact = ({ onUnlock }) => {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [otpStage, setOtpStage] = useState(false);
  const [otp, setOtp] = useState("");
  const businessOpen = isBusinessHours();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads/request-otp`, form);
      toast.success(`Verification code sent to ${form.email}`);
      setOtpStage(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try WhatsApp instead."
      );
    } finally {
      setSending(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads/verify-otp`, {
        email: form.email,
        code: otp,
      });
      toast.success("Verified — enquiry sent. We'll reach out within 24 hours.");
      setForm(empty);
      setOtp("");
      setOtpStage(false);
      setSubmitted(true);
      onUnlock?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Verification failed — please try again."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 lg:py-40 bg-midnight"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-xs font-bold uppercase tracking-[0.25em] text-coal/40"
            >
              07 — Contact
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-900 tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl mt-4"
            >
              Let's put your brand{" "}
              <span className="font-accent italic font-medium text-terracotta">
                on the shelf.
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 text-base sm:text-lg leading-relaxed text-coal/60 max-w-md"
            >
              Tell us where you are and where you want to sell. We reply within
              24 hours with an honest read on your fastest path to launch.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="mt-12 space-y-6"
            >
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="flex items-center gap-4 group w-fit"
              >
                <span className="w-11 h-11 border border-coal/15 flex items-center justify-center group-hover:border-terracotta group-hover:bg-terracotta/10 transition-colors duration-300">
                  <MessageCircle size={18} className="text-terracotta" />
                </span>
                <span className="text-sm text-coal/70 group-hover:text-coal transition-colors duration-300">
                  WhatsApp — fastest response
                </span>
              </a>
              <a
                href={`mailto:${ADMIN_EMAIL}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 group w-fit"
              >
                <span className="w-11 h-11 border border-coal/15 flex items-center justify-center group-hover:border-terracotta group-hover:bg-terracotta/10 transition-colors duration-300">
                  <Mail size={18} className="text-terracotta" />
                </span>
                <span className="text-sm text-coal/70 group-hover:text-coal transition-colors duration-300">
                  {ADMIN_EMAIL}
                </span>
              </a>
              <div className="flex items-center gap-4" data-testid="contact-location">
                <span className="w-11 h-11 border border-coal/15 flex items-center justify-center">
                  <MapPin size={18} className="text-terracotta" />
                </span>
                <span className="text-sm text-coal/70">
                  {OFFICE_ADDRESS} — serving brands PAN India
                </span>
              </div>
            </motion.div>
          </div>

          {submitted ? (
            <motion.div
              data-testid="contact-success-panel"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="border border-coal/10 p-8 lg:p-12 bg-ink/50 flex flex-col items-start justify-center"
            >
              <CheckCircle2 size={44} strokeWidth={1.5} className="text-terracotta" />
              <h3 className="font-display font-900 tracking-tight text-3xl sm:text-4xl mt-8">
                Enquiry received.
              </h3>
              <p className="mt-4 text-base leading-relaxed text-coal/60 max-w-sm">
                We'll reach out within 24 hours. Need us sooner? Call us
                directly — we'd love to hear about your brand.
              </p>
              {businessOpen ? (
                <>
                  <a
                    href={PHONE_TEL}
                    data-testid="contact-success-call-link"
                    className="mt-8 font-display font-900 tracking-tight text-3xl sm:text-4xl text-terracotta hover:text-gold transition-colors duration-300"
                  >
                    {PHONE_DISPLAY}
                  </a>
                  <p
                    data-testid="contact-success-hours"
                    className="mt-3 text-xs font-bold uppercase tracking-[0.2em] text-coal/45"
                  >
                    Calling hours: {BUSINESS_HOURS}
                  </p>
                </>
              ) : (
                <p
                  data-testid="contact-success-offhours"
                  className="mt-8 text-sm leading-relaxed text-coal/60 max-w-sm border border-coal/15 px-5 py-4"
                >
                  It's currently outside calling hours ({BUSINESS_HOURS} IST) —
                  WhatsApp us anytime and we'll reply first thing.
                </p>
              )}
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-success-whatsapp-btn"
                  className="inline-flex items-center gap-2.5 bg-terracotta text-night font-bold text-sm uppercase tracking-[0.15em] px-7 py-4 hover:bg-gold transition-colors duration-300"
                >
                  <MessageCircle size={17} strokeWidth={2.5} />
                  WhatsApp Instead
                </a>
                <button
                  data-testid="contact-success-another-btn"
                  onClick={() => setSubmitted(false)}
                  className="inline-flex items-center gap-2.5 border border-coal/25 text-coal font-bold text-sm uppercase tracking-[0.15em] px-7 py-4 hover:border-terracotta hover:text-terracotta transition-colors duration-300"
                >
                  Send Another Enquiry
                </button>
                <button
                  data-testid="contact-success-founder-btn"
                  onClick={() => scrollToSection("#founder")}
                  className="inline-flex items-center gap-2.5 border border-coal/25 text-coal font-bold text-sm uppercase tracking-[0.15em] px-7 py-4 hover:border-terracotta hover:text-terracotta transition-colors duration-300"
                >
                  Meet the Founder
                </button>
              </div>
            </motion.div>
          ) : otpStage ? (
            <motion.form
              onSubmit={verify}
              data-testid="otp-form"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="border border-coal/10 p-8 lg:p-12 bg-ink/50"
            >
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-gold">
                Verify your email
              </span>
              <h3 className="font-display font-900 tracking-tight text-3xl sm:text-4xl mt-4">
                Enter the 6-digit code
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-coal/60">
                We sent a verification code to{" "}
                <span className="font-bold text-coal">{form.email}</span>. It
                expires in 10 minutes.
              </p>
              <input
                data-testid="otp-input"
                required
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="••••••"
                className="mt-8 w-full bg-transparent border border-coal/15 px-5 py-5 text-center font-display font-900 text-3xl tracking-[0.5em] text-coal placeholder:text-coal/20 focus:outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta transition-colors duration-300"
              />
              <motion.button
                type="submit"
                data-testid="otp-verify-btn"
                disabled={sending || otp.length !== 6}
                whileHover={{ scale: sending ? 1 : 1.02 }}
                whileTap={{ scale: sending ? 1 : 0.98 }}
                className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-terracotta text-night font-bold text-sm uppercase tracking-[0.15em] px-8 py-5 hover:bg-gold disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
              >
                {sending ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify & Send Enquiry
                    <ArrowRight size={18} strokeWidth={2.5} />
                  </>
                )}
              </motion.button>
              <div className="mt-6 flex items-center justify-between text-xs">
                <button
                  type="button"
                  data-testid="otp-back-btn"
                  onClick={() => setOtpStage(false)}
                  className="font-bold uppercase tracking-[0.15em] text-coal/45 hover:text-terracotta transition-colors duration-300"
                >
                  ← Edit details
                </button>
                <button
                  type="button"
                  data-testid="otp-resend-btn"
                  onClick={submit}
                  className="font-bold uppercase tracking-[0.15em] text-coal/45 hover:text-terracotta transition-colors duration-300"
                >
                  Resend code
                </button>
              </div>
            </motion.form>
          ) : (
            <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="border border-coal/10 p-8 lg:p-12 bg-ink/50"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="lead-name" className="block text-xs font-bold uppercase tracking-[0.2em] text-coal/45 mb-2.5">
                  Name *
                </label>
                <input
                  id="lead-name"
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your full name"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="lead-phone" className="block text-xs font-bold uppercase tracking-[0.2em] text-coal/45 mb-2.5">
                  Phone *
                </label>
                <input
                  id="lead-phone"
                  data-testid="contact-phone-input"
                  required
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+91 ..."
                  className={inputCls}
                />
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="lead-email" className="block text-xs font-bold uppercase tracking-[0.2em] text-coal/45 mb-2.5">
                Email *
              </label>
              <input
                id="lead-email"
                type="email"
                data-testid="contact-email-input"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="you@brand.com"
                className={inputCls}
              />
            </div>
            <div className="mt-5">
              <label htmlFor="lead-brand" className="block text-xs font-bold uppercase tracking-[0.2em] text-coal/45 mb-2.5">
                Brand / Company
              </label>
              <input
                id="lead-brand"
                data-testid="contact-brand-input"
                value={form.brand}
                onChange={set("brand")}
                placeholder="Your brand name"
                className={inputCls}
              />
            </div>
            <div className="mt-5">
              <label className="block text-xs font-bold uppercase tracking-[0.2em] text-coal/45 mb-2.5">
                Interested in
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="package-selector">
                {PACKAGES.map((p, i) => (
                  <button
                    key={p}
                    type="button"
                    data-testid={`package-option-${i}`}
                    onClick={() => setForm({ ...form, package: p })}
                    className={`text-left text-xs font-bold tracking-wide px-4 py-3.5 border transition-colors duration-300 ${
                      form.package === p
                        ? "border-terracotta bg-terracotta/10 text-coal"
                        : "border-coal/15 text-coal/50 hover:border-coal/35 hover:text-coal/75"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="lead-message" className="block text-xs font-bold uppercase tracking-[0.2em] text-coal/45 mb-2.5">
                Message *
              </label>
              <textarea
                id="lead-message"
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder="What do you sell, and where do you want to launch?"
                className={`${inputCls} resize-none`}
              />
            </div>
            <motion.button
              type="submit"
              data-testid="contact-submit-btn"
              disabled={sending}
              whileHover={{ scale: sending ? 1 : 1.02 }}
              whileTap={{ scale: sending ? 1 : 0.98 }}
              className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-terracotta text-night font-bold text-sm uppercase tracking-[0.15em] px-8 py-5 hover:bg-gold disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
            >
              {sending ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  Send Enquiry
                  <ArrowRight size={18} strokeWidth={2.5} />
                </>
              )}
            </motion.button>
            <p className="mt-4 text-xs text-coal/35 text-center">
              No spam, no obligation — just a plan.
            </p>
          </motion.form>
          )}
        </div>
      </div>
    </section>
  );
};
